import {useState, Dispatch, SetStateAction} from 'react'

import styles from './PeriodSelect.module.scss'
import {
  Form,
  FormItem,
  Button,
  Dropdown,
  Text,
  RangePicker,
  Modal,
  Box,
} from 'common/components'
import {useOpen} from 'common/hooks'
import {FIELD_RULES} from 'common/constants'
import {getCurrentPeriod} from '..'

interface PeriodSelectProps extends SetFunctions {
  periods: any[]
  periodKey: string
  customPeriod: Date[]
}

interface PickerFormProps extends SetFunctions {
  customPeriod: any[]
  onPickerClose: () => void
  onDropdownClose: () => void
}

interface SetFunctions {
  setPeriodKey: Dispatch<SetStateAction<string>>
  setCustomPeriod: Dispatch<SetStateAction<any[]>>
}

export default function PeriodSelect({
  periods,
  periodKey,
  customPeriod,
  setPeriodKey,
  setCustomPeriod,
}: PeriodSelectProps) {
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [isPickerOpen, handlePickerOpen, handlePickerClose] = useOpen()

  const handleSelect = ({key}: any) => {
    if (isCustomPeriod(key)) {
      return handlePickerOpen()
    }
    setPeriodKey(key)
    handleDropdownClose()
  }

  const handleDropdownClose = () => setDropdownOpen(false)

  const currentPeriod = getCurrentPeriod(periods, periodKey)
  const isCustom = isCustomPeriod(periodKey)

  return (
    <>
      <Dropdown
        overlayClassName={styles.overlay}
        open={isDropdownOpen}
        menu={{
          items: periods,
          selectable: true,
          selectedKeys: [periodKey],
          onClick: handleSelect,
        }}
        onOpenChange={isPickerOpen ? undefined : setDropdownOpen}
      >
        <Button dropdown variant="secondary">
          <Text lowercase={!isCustom} as="span">
            for {isCustom ? currentPeriod.tip : currentPeriod.label}
          </Text>
        </Button>
      </Dropdown>
      <Modal
        narrow
        maskStyle={popupStyle}
        wrapClassName={styles.modalWrap}
        title="Custom period"
        open={isPickerOpen}
        onCancel={handlePickerClose}
      >
        <PickerForm
          customPeriod={customPeriod}
          setPeriodKey={setPeriodKey}
          setCustomPeriod={setCustomPeriod}
          onPickerClose={handlePickerClose}
          onDropdownClose={handleDropdownClose}
        />
      </Modal>
    </>
  )
}

const PickerForm = ({
  customPeriod,
  setPeriodKey,
  setCustomPeriod,
  onPickerClose,
  onDropdownClose,
}: PickerFormProps) => {
  const handleFinish = (values: any) => {
    setCustomPeriod(values.period)
    setPeriodKey('custom')
    onPickerClose()
    onDropdownClose()
  }

  const initialValues = {period: customPeriod}

  return (
    <Form initialValues={initialValues} onFinish={handleFinish}>
      <FormItem name="period" rules={FIELD_RULES}>
        <RangePicker autoFocus popupStyle={popupStyle} />
      </FormItem>
      <Box align="center">
        <Button htmlType="submit">Select</Button>
      </Box>
    </Form>
  )
}

const isCustomPeriod = (key: string): boolean => key === 'custom'

const popupStyle = {zIndex: 100000}
